import React from 'react'
import {
  Box,
  InputBase,
  type InputBaseProps,
  InputLabel,
  type OutlinedInputProps,
  type SxProps,
  styled,
} from '@mui/material'

const TextFieldBase = styled(InputBase)(({ theme, error, disabled }) => ({
  width: '100%',
  'label + &': {
    marginTop: theme.spacing(3),
  },
  borderRadius: 4,
  backgroundColor: disabled ? theme.palette.action.disabledInput || '#f5f5f5' : 'white',
  boxShadow: disabled
    ? `inset 0 0 0 1px ${theme.palette.text.disabled || '#bdbdbd'}`
    : error
      ? `inset 0 0 0 2px ${theme.palette.error.main}`
      : `inset 0 0 0 1px ${theme.palette.input?.default || '#e0e0e0'}`,
  transition: theme.transitions.create(['box-shadow']),

  '&:hover': !disabled && {
    boxShadow: error
      ? `inset 0 0 0 2px ${theme.palette.error.main}`
      : `inset 0 0 0 2px ${theme.palette.input?.hover || theme.palette.primary.main}`,
  },

  '&:focus-within': !disabled && {
    boxShadow: `inset 0 0 0 2px ${theme.palette.input?.hover || theme.palette.primary.main}, 0 0 0 2px ${theme.palette.input?.focus || theme.palette.primary.light}`,
  },
  '&.MuiInputBase-adornedStart, &.MuiInputBase-adornedEnd': {
    display: 'flex',
    alignItems: 'center',
  },

  '& .MuiInputBase-input': {
    fontSize: 16,
    padding: '8.5px 12px',
    fontFamily: theme.typography.body2.fontFamily,
    border: 'none',
    backgroundColor: 'transparent',
    color: disabled ? theme.palette.text.secondary : theme.palette.text.primary,
    outline: 'none',
    width: '100%',
    '&::placeholder': {
      color: theme.palette.text.secondary,
      opacity: 1,
    },
    '&:focus': {
      outline: 'none',
    },
  },

  '& .MuiInputAdornment-root': {
    color: theme.palette.text.secondary,
    maxHeight: 'none',
  },
  '& .MuiInputAdornment-positionStart': {
    marginLeft: '12px',
    marginRight: '8px',
  },
  '& .MuiInputAdornment-positionEnd': {
    marginLeft: '8px',
    marginRight: '12px',
  },
  '&.MuiInputBase-adornedStart .MuiInputBase-input': {
    paddingLeft: '4px',
    flex: 1,
    minWidth: 0,
  },
  '&.MuiInputBase-adornedEnd .MuiInputBase-input': {
    paddingRight: '4px',
    flex: 1,
    minWidth: 0,
  },
}))

const InputLabelStyled = styled(InputLabel)(({ theme }) => ({
  fontSize: 16,
  letterSpacing: theme.typography.body2.letterSpacing,
  fontFamily: theme.typography.fontFamily,
  '&.Mui-focused': {
    color: theme.palette.text.primary,
  },
}))

interface TextFieldProps extends InputBaseProps {
  label?: string
  helperText?: string
  errorText?: string
  id?: string
  variant?: 'outlined' | 'filled' | 'standard' // Add back variant prop for compatibility
  size?: 'small' | 'medium' // Add back size prop for compatibility
  containerStyle?: SxProps
  InputProps?: Partial<OutlinedInputProps> // Add back InputProps for adornments and other features
  InputLabelProps?: React.ComponentProps<typeof InputLabel>
  inputRef?: React.Ref<HTMLInputElement | HTMLTextAreaElement>
}

const TextField = React.forwardRef<HTMLDivElement, TextFieldProps>(
  (
    {
      label,
      id,
      error,
      disabled,
      multiline,
      rows,
      variant = 'outlined', // eslint-disable-line
      size = 'medium', // eslint-disable-line
      containerStyle,
      InputProps,
      InputLabelProps,
      inputRef,
      ...props
    },
    ref,
  ) => {
    const inputPropsRef = InputProps?.ref
    const combinedRef = (node: HTMLDivElement | null): void => {
      if (ref) {
        if (typeof ref === 'function') {
          ref(node)
        } else {
          ;(ref as React.MutableRefObject<HTMLDivElement | null>).current = node
        }
      }
      if (inputPropsRef) {
        if (typeof inputPropsRef === 'function') {
          inputPropsRef(node)
        } else {
          ;(inputPropsRef as React.MutableRefObject<HTMLDivElement | null>).current = node
        }
      }
      if (inputRef) {
        if (typeof inputRef === 'function') {
          inputRef(node as HTMLInputElement | HTMLTextAreaElement | null)
        } else {
          ;(
            inputRef as React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>
          ).current = node as HTMLInputElement | HTMLTextAreaElement | null
        }
      }
    }

    return (
      <Box sx={containerStyle}>
        {label && (
          <InputLabelStyled
            htmlFor={id || 'text-field'}
            shrink
            sx={{
              fontSize: 20,
              color: disabled ? 'text.disabled' : 'text.secondary',
              '&.Mui-focused': {
                color: disabled ? 'text.secondary' : 'text.primary',
              },
            }}
            {...InputLabelProps}
          >
            {label}
          </InputLabelStyled>
        )}
        <TextFieldBase
          id={id || 'text-field'}
          error={error}
          disabled={disabled}
          multiline={multiline}
          rows={rows}
          ref={combinedRef}
          startAdornment={InputProps?.startAdornment}
          endAdornment={InputProps?.endAdornment}
          className={InputProps?.className}
          onMouseDown={InputProps?.onMouseDown}
          {...props}
          inputProps={props.inputProps}
        />
      </Box>
    )
  },
)

export default TextField
